import type { SaveData } from "../../domain/types";

const KEY = "treasure-grid-save-v2";
export const CURRENT_SAVE_VERSION = 7;

export interface StoragePort {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

const id = (): string => globalThis.crypto?.randomUUID?.() ??
  `player-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function createDefaultSave(): SaveData {
  const now = new Date().toISOString();
  return {
    version: 7,
    playerId: id(),
    createdAt: now,
    updatedAt: now,
    progression: {
      campaignLevel: 1, explorerLevel: 1, xp: 0, completedLevels: [],
      unlockedWorlds: ["forgotten-ruins"], bestScore: 0, runs: 0, totalTreasuresCollected: 0,
      levelStars: {}, milestoneRewardsClaimed: [],
    },
    economy: { gold: 0, gems: 0, relicDust: 0, relics: 0, masteryPoints: 0 },
    inventory: [],
    settings: {
      musicVolume: 0.7, sfxVolume: 0.8, hapticsEnabled: true,
      reducedMotion: false, highContrast: false, tutorialCompleted: false,
      colorBlindSymbols: true, cosmeticTheme: "temple",
    },
    achievements: [],
    achievementUnlockedAt: {},
    daily: { lastPlayedDate: null, completedDates: [], lastRewardDate: null, streak: 0 },
    relicFragments: 0,
    unlockedRelics: [],
  };
}

type MutableSave = Record<string, unknown>;

function migrateLegacy(value: MutableSave): MutableSave {
  if (typeof value.version === "number") return value;
  const fresh = createDefaultSave();
  fresh.progression.campaignLevel = Number(value.level ?? 1);
  fresh.progression.xp = Number(value.xp ?? 0);
  fresh.progression.bestScore = Number(value.bestScore ?? 0);
  fresh.progression.runs = Number(value.runs ?? 0);
  fresh.economy.gold = Number(value.gold ?? 0);
  fresh.economy.gems = Number(value.gems ?? 0);
  fresh.economy.relics = Number(value.relics ?? 0);
  return { ...fresh, version: 1 };
}

function migrateV1ToV2(value: MutableSave): MutableSave {
  return { ...value, version: 2 };
}

function migrateV2ToV3(value: MutableSave): MutableSave {
  const progression = (value.progression ?? {}) as Record<string, unknown>;
  const settings = (value.settings ?? {}) as Record<string, unknown>;
  const xp = Number(progression.xp ?? 0);
  let explorerLevel = 1;
  while (xp >= Math.floor(100 * Math.pow(explorerLevel, 1.5))) explorerLevel++;
  return {
    ...value,
    version: 3,
    progression: {
      ...progression,
      explorerLevel,
      unlockedWorlds: progression.unlockedWorlds ?? ["forgotten-ruins"],
    },
    settings: { ...settings, colorBlindSymbols: settings.colorBlindSymbols ?? true },
    achievements: value.achievements ?? [],
    daily: value.daily ?? { lastPlayedDate: null, completedDates: [] },
  };
}

function migrateV3ToV4(value: MutableSave): MutableSave {
  return {
    ...value,
    version: 4,
    relicFragments: value.relicFragments ?? 0,
    unlockedRelics: value.unlockedRelics ?? [],
  };
}

function migrateV4ToV5(value: MutableSave): MutableSave {
  const progression = (value.progression ?? {}) as Record<string, unknown>;
  const daily = (value.daily ?? {}) as Record<string, unknown>;
  return {
    ...value,
    version: 5,
    progression: { ...progression, totalTreasuresCollected: progression.totalTreasuresCollected ?? 0 },
    daily: { ...daily, lastRewardDate: daily.lastRewardDate ?? null, streak: daily.streak ?? 0 },
  };
}

function migrateV5ToV6(value: MutableSave): MutableSave {
  const progression = (value.progression ?? {}) as Record<string, unknown>;
  const settings = (value.settings ?? {}) as Record<string, unknown>;
  return {
    ...value, version: 6,
    progression: { ...progression, levelStars: progression.levelStars ?? {}, milestoneRewardsClaimed: progression.milestoneRewardsClaimed ?? [] },
    settings: { ...settings, cosmeticTheme: settings.cosmeticTheme ?? "temple" },
  };
}

function migrateV6ToV7(value: MutableSave): MutableSave {
  return {
    ...value,
    version: 7,
    achievementUnlockedAt: value.achievementUnlockedAt ?? {},
  };
}

export function migrateSave(value: unknown): SaveData {
  if (!value || typeof value !== "object") return createDefaultSave();
  let save = migrateLegacy(structuredClone(value) as MutableSave);
  let version = Number(save.version ?? 1);
  while (version < CURRENT_SAVE_VERSION) {
    if (version === 1) save = migrateV1ToV2(save);
    else if (version === 2) save = migrateV2ToV3(save);
    else if (version === 3) save = migrateV3ToV4(save);
    else if (version === 4) save = migrateV4ToV5(save);
    else if (version === 5) save = migrateV5ToV6(save);
    else if (version === 6) save = migrateV6ToV7(save);
    else throw new Error(`Unsupported save version ${version}`);
    version = Number(save.version);
  }
  const defaults = createDefaultSave();
  return {
    ...defaults, ...save,
    version: CURRENT_SAVE_VERSION,
    progression: { ...defaults.progression, ...(save.progression as object) },
    economy: { ...defaults.economy, ...(save.economy as object) },
    settings: { ...defaults.settings, ...(save.settings as object) },
    daily: { ...defaults.daily, ...(save.daily as object) },
    inventory: Array.isArray(save.inventory) ? save.inventory as SaveData["inventory"] : [],
    achievements: Array.isArray(save.achievements) ? save.achievements as string[] : [],
    achievementUnlockedAt: typeof save.achievementUnlockedAt === "object" && save.achievementUnlockedAt !== null
      ? save.achievementUnlockedAt as Record<string, string>
      : {},
    relicFragments: Number(save.relicFragments ?? 0),
    unlockedRelics: Array.isArray(save.unlockedRelics) ? save.unlockedRelics as string[] : [],
  } as SaveData;
}

export class SaveSystem {
  constructor(private readonly storage: StoragePort = localStorage) {}
  load(): SaveData {
    try { return migrateSave(JSON.parse(this.storage.getItem(KEY) ?? "null")); }
    catch { return createDefaultSave(); }
  }
  save(data: SaveData): void {
    data.version = CURRENT_SAVE_VERSION;
    data.updatedAt = new Date().toISOString();
    this.storage.setItem(KEY, JSON.stringify(data));
  }
  reset(): SaveData {
    this.storage.removeItem?.(KEY);
    const fresh = createDefaultSave();
    this.save(fresh);
    return fresh;
  }
}
